import { AnimatePresence, motion, useScroll, useMotionValueEvent } from "framer-motion";
import { useState } from "react";
import CtaButton from "@/components/CtaButton";
import { Sparkles, PlayCircle, ArrowRight } from "lucide-react";

// Pinned strip that appears once the visitor scrolls past the hero. When
// `primaryMode === "demo"`, the fee is hidden and the Book a demo action is
// highlighted as the main CTA (gradient background, bigger, first in the
// layout) — with the enquire button as secondary.
export default function StickyOfferBar({
  show,
  fact,
  factLabel = "From",
  onEnquire,
  demo,
  onDemoEnquire,
  onViewPlans,
  hideEnquire = false, // true once this course has a real paid path — Enquire is never the only option
  primaryMode = "enquire", // "enquire" | "demo"
  testId = "sticky-offer-bar",
}) {
  const isDemoMode = primaryMode === "demo";

  const ViewPlansLink = ({ full, primary }) =>
    onViewPlans ? (
      <button
        type="button"
        onClick={onViewPlans}
        data-testid={`${testId}-view-plans`}
        className={
          primary
            ? `inline-flex items-center justify-center gap-2 rounded-full bg-emerald text-white font-sans text-sm font-bold px-6 py-3 hover:bg-emeraldhover transition-colors whitespace-nowrap ${full ? "w-full" : ""}`
            : `inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-emerald text-emerald font-sans text-xs md:text-sm font-bold px-4 py-2.5 hover:bg-emerald/5 transition-colors whitespace-nowrap ${full ? "w-full" : ""}`
        }
      >
        View plans ↓
      </button>
    ) : null;

  const DemoButton = ({ full, primary }) => {
    const base =
      "inline-flex items-center justify-center gap-2 rounded-full font-sans font-bold px-5 whitespace-nowrap transition-all duration-300";
    const style = primary
      ? `${base} bg-gradient-to-r from-terracotta via-amber-500 to-lemon text-ink py-3.5 shadow-xl shadow-terracotta/30 hover:shadow-2xl hover:shadow-terracotta/40 hover:-translate-y-0.5 text-[14px] md:text-[15px]`
      : `${base} border-2 border-terracotta text-terracotta py-2.5 hover:bg-terracotta/5 text-[13px] md:text-sm`;
    const demoFallback = primary ? "Book a demo · ₹99" : `Register — ${demo?.fee ?? "₹99"}`;
    const demoLabel = primary
      ? `Book a demo · ${demo?.fee ?? "₹99"}`
      : `Register — ${demo?.fee ?? "₹99"}`;
    return (
      <button
        type="button"
        onClick={onDemoEnquire || onEnquire}
        data-testid={`${testId}-demo-cta`}
        className={`${style} ${full ? "flex-1 w-full" : ""}`}
      >
        {primary ? <PlayCircle size={16} fill="currentColor" /> : <Sparkles size={14} />}
        {primary ? demoLabel : demoFallback}
      </button>
    );
  };

  const EnquireButton = ({ full, primary }) => {
    if (primary) {
      return (
        <CtaButton
          onEnquire={onEnquire}
          size="sm"
          full={full}
          testId={`${testId}-cta`}
          label="Enquire now"
        />
      );
    }
    const base =
      "inline-flex items-center justify-center gap-2 rounded-full border-2 border-ink/20 bg-white/70 backdrop-blur text-ink font-sans text-sm font-bold px-5 py-2.5 hover:bg-white hover:border-ink/40 transition-all duration-300 whitespace-nowrap";
    return (
      <button
        type="button"
        onClick={onEnquire}
        data-testid={`${testId}-cta`}
        className={`${base} ${full ? "w-full flex-1" : ""}`}
      >
        Enquire
        <ArrowRight size={14} />
      </button>
    );
  };

  const showFee = !isDemoMode && fact && typeof fact === "string" && /₹/.test(fact);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid={testId}
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className={`fixed bottom-0 left-0 right-0 z-40 bg-surface/95 backdrop-blur border-t border-line shadow-2xl`}
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <div className="w-full mx-auto px-4 md:px-8 py-3 md:py-4">
            {/* desktop/tablet row */}
            <div className="hidden sm:flex items-center gap-4 md:gap-6">
              {showFee && (
                <div className="shrink-0 pr-4 md:pr-6 border-r border-line">
                  <p className="font-mono text-[10px] md:text-[11px] uppercase tracking-widest text-graphite">{factLabel}</p>
                  <p className="font-display font-bold text-emerald text-lg md:text-2xl leading-tight whitespace-nowrap">{fact}</p>
                </div>
              )}
              {!showFee && isDemoMode && (
                <div className="shrink-0 flex items-center gap-3 pr-4 md:pr-6">
                  <span className="w-10 h-10 rounded-2xl bg-gradient-to-br from-terracotta to-amber-500 text-white flex items-center justify-center shadow-md">
                    <Sparkles size={18} />
                  </span>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-terracotta">Demo highlighted</p>
                    <p className="font-display font-bold text-ink text-sm md:text-base leading-tight whitespace-nowrap">
                      Try it live for {demo?.fee ?? "₹99"}
                    </p>
                  </div>
                </div>
              )}

              {!isDemoMode && demo && (
                <>
                  <div className="h-9 w-px bg-line shrink-0" />
                  <div className="shrink-0">
                    <p className="font-mono text-[10px] md:text-[11px] uppercase tracking-widest text-terracotta">Demo session</p>
                    <p className="font-display font-bold text-terracotta text-lg md:text-2xl leading-tight whitespace-nowrap">
                      {demo.fee}
                    </p>
                  </div>
                </>
              )}

              <div className="ml-auto flex items-center gap-2 md:gap-3 shrink-0">
                {isDemoMode ? (
                  <>
                    <ViewPlansLink />
                    <DemoButton primary />
                    {!hideEnquire && <EnquireButton />}
                  </>
                ) : (
                  <>
                    {demo && <DemoButton />}
                    {hideEnquire ? (
                      <ViewPlansLink primary />
                    ) : (
                      <>
                        <ViewPlansLink />
                        <EnquireButton primary />
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* mobile: full-width stacked so both CTAs are big */}
            <div className="flex sm:hidden flex-col gap-2.5">
              {showFee && (
                <div className="flex items-center justify-between px-1">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-graphite">{factLabel}</p>
                  <p className="font-display font-bold text-emerald text-lg leading-tight whitespace-nowrap">{fact}</p>
                </div>
              )}
              {isDemoMode ? (
                <>
                  <DemoButton full primary />
                  <div className="flex gap-2.5">
                    <ViewPlansLink full={hideEnquire} />
                    {!hideEnquire && <EnquireButton full />}
                  </div>
                </>
              ) : (
                <>
                  {demo && <DemoButton full />}
                  {hideEnquire ? (
                    <ViewPlansLink full primary />
                  ) : (
                    <>
                      <ViewPlansLink full />
                      <EnquireButton full primary />
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Hook: becomes true once the visitor has scrolled past `pastPx`.
export function useStickyAfter(pastPx = 520) {
  const [past, setPast] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (y) => setPast(y > pastPx));
  return past;
}
